import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Store auth-v2 — tokens + identité d'authentification (distincte de l'entité
 * métier `User` qui est dans `stores/auth.store.ts`).
 *
 * Stratégie de stockage :
 *  - `accessToken` n'est PAS persisté (uniquement en mémoire). En cas de reload,
 *    on tente un /refresh avec le refreshToken persisté pour récupérer un access.
 *  - `refreshToken` est persisté dans localStorage (sous une clé dédiée pour
 *    cohabiter avec d'autres états). Compromis pragmatique : l'idéal serait un
 *    cookie httpOnly côté API ; documenté pour Phase 4.
 *  - `identity` (id, email, …) persisté pour éviter un flash UI au démarrage.
 */

export interface AuthV2Identity {
  id: string;
  email: string;
  emailVerified: boolean;
  locale: string;
}

interface AuthV2State {
  accessToken: string | null;
  refreshToken: string | null;
  accessExpiresAt: number | null;
  identity: AuthV2Identity | null;
  /** Réinitialisé à chaque chargement de page (process restart). */
  hydrated: boolean;
  setSession: (s: {
    accessToken: string;
    refreshToken: string;
    accessExpiresIn: number;
    identity: AuthV2Identity;
  }) => void;
  updateAccess: (s: { accessToken: string; refreshToken: string; accessExpiresIn: number }) => void;
  setIdentity: (identity: AuthV2Identity) => void;
  setHydrated: (v: boolean) => void;
  clear: () => void;
}

export const useAuthV2Store = create<AuthV2State>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      accessExpiresAt: null,
      identity: null,
      hydrated: false,
      setSession: ({ accessToken, refreshToken, accessExpiresIn, identity }) =>
        set({
          accessToken,
          refreshToken,
          accessExpiresAt: Date.now() + accessExpiresIn * 1_000,
          identity,
        }),
      updateAccess: ({ accessToken, refreshToken, accessExpiresIn }) =>
        set({
          accessToken,
          refreshToken,
          accessExpiresAt: Date.now() + accessExpiresIn * 1_000,
        }),
      setIdentity: (identity) => set({ identity }),
      setHydrated: (v) => set({ hydrated: v }),
      clear: () =>
        set({
          accessToken: null,
          refreshToken: null,
          accessExpiresAt: null,
          identity: null,
        }),
    }),
    {
      name: "cliently.auth-v2",
      storage: createJSONStorage(() => localStorage),
      // On ne persiste PAS l'access token (vol XSS = window très courte).
      partialize: (state) => ({
        refreshToken: state.refreshToken,
        identity: state.identity,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      },
    },
  ),
);
